import { memo } from "react";
import { Box } from "../../../common/components";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { ComboItems } from "../../../types";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import CartItemSizeSelector from "./CartItemSizeSelector";
import CartItemWarnings from "../Components/CartItemWarnings";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CartItemPrice from "../Components/CartItemPrice";

const styles = createStyles({
  container: {
    backgroundColor: "white",
    border: `1px solid ${palette.grey.lightGrey}`,
    borderRadius: "8px",
    ".cartItemSizeSelector--bundle": {
      "&:first-of-type": {
        borderBottomLeftRadius: "0px",
        borderBottomRightRadius: "0px",
        borderBottom: "none",
      },
      "&:last-of-type": {
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderTop: "none",
      },
    },
  },
  bundleHeader: {
    p: spacing.groupingMobile,
    borderRadius: "4px 4px 0 0",
    background: palette.grey.lightBackground,
    overflow: "hidden",
  },
});

interface Props {
  comboItems: ComboItems;
  skipBundle?: boolean;
  onRemove: (id: string, productName: string) => void;
}

const CartComboItemSizeSelector = (props: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useThemeBreakpoints();
  const { onRemove, comboItems } = props;

  const isBundle = comboItems.items?.length > 1;

  //Bundle desktop
  if (isBundle) {
    if (!isMobile) {
      return (
        <Box
          display={"flex"}
          gap={spacing.mediumDesktop}
          flexDirection={"column"}
        >
          {comboItems.items?.map((item, index) => (
            <CartItemSizeSelector
              key={`${item.cartItemId}-${index}`}
              item={item}
              onRemove={onRemove}
              comboItems={comboItems}
            />
          ))}
        </Box>
      );
    } else {
      const comboName = isBundle
        ? t("specs.bundleName", {
            familyName: t(`specs.bundleFamily.${comboItems.familyName}`)[
              i18n.language === "fr" ? "toLowerCase" : "toString"
            ](),
          })
        : "";

      return (
        <Box
          sx={styles.container}
          data-test={`comboItem.${comboItems.familyName}`}
        >
          <Box sx={styles.bundleHeader}>
            <Typography
              variant="textRegularBold"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {comboName}
            </Typography>
          </Box>
          <Box sx={{ p: spacing.wrapperMobile }}>
            <CartItemWarnings
              deprecatedDesign={false}
              item={comboItems.items[0]}
              comboItems={comboItems}
              forCartItem={true}
            />
            <CartItemPrice comboItems={comboItems} item={comboItems.items[0]} />
          </Box>
          {comboItems.items?.map((item, index) => (
            <CartItemSizeSelector
              key={`${item.cartItemId}-${index}`}
              item={item}
              onRemove={onRemove}
              comboItems={comboItems}
            />
          ))}
        </Box>
      );
    }
  }

  return (
    <Box sx={styles.container} data-test={`comboItem.${comboItems.familyName}`}>
      {comboItems.items?.map((item, index) => (
        <CartItemSizeSelector
          key={`${item.cartItemId}-${index}`}
          item={item}
          onRemove={onRemove}
          comboItems={comboItems}
          showBundleHeader={isMobile && isBundle && index === 0}
        />
      ))}
    </Box>
  );
};

export default memo(CartComboItemSizeSelector);
